import { useState, useEffect } from "react";
import { Menu, X, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollSpy } from "@/hooks/useScrollSpy";
import { useScrollToSection } from "@/hooks/useScrollToSection";
import mesjidLogo from "@/assets/mesjid-logo-new.png";

const navLinks = [
  { label: "Prayer Times", href: "#prayer-times", sectionId: "prayer-times" },
  { label: "Announcements", href: "#announcements", sectionId: "announcements" },
  { label: "Services", href: "#services", sectionId: "services" },
  { label: "Contact", href: "#contact", sectionId: "contact" },
  { label: "Directions", href: "#directions", sectionId: "directions" },
];

const sectionIds = navLinks.map(link => link.sectionId);

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const activeSection = useScrollSpy({ sectionIds, offset: 150 });

  const { scrollToSection: scrollTo, scrollToTop } = useScrollToSection();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (href: string) => {
    setIsOpen(false);
    scrollTo(href);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled 
        ? "bg-card/95 backdrop-blur-md shadow-lg border-b border-gold/10" 
        : "bg-transparent"
    }`}>
      <div className="container-mosque">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          {/* Logo */}
          <a 
            href="#" 
            onClick={e => {
              e.preventDefault();
              scrollToTop();
            }}
            className="flex items-center gap-2 group"
          >
            <div className="relative h-12 md:h-14 transition-transform duration-300 group-hover:scale-105">
              <img 
                src={mesjidLogo} 
                alt="Mesjid Al Sabur" 
                className="h-full w-auto object-contain"
                width={48}
                height={48}
              />
            </div>
            <span className={`text-lg md:text-xl font-black tracking-tight transition-colors duration-300 drop-shadow-sm ${
              scrolled ? 'text-primary' : 'text-white'
            }`}>
              Mesjid Al Sabur
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map(link => {
              const isActive = activeSection === link.sectionId;
              return (
                <button 
                  key={link.href} 
                  onClick={() => scrollToSection(link.href)} 
                  className={`nav-link font-semibold text-[15px] relative transition-all duration-300 ${
                    !scrolled && 'text-white hover:text-gold'
                  } ${isActive ? 'text-gold' : ''}`}
                >
                  {link.label}
                  {/* Active indicator */}
                  <span 
                    className={`absolute bottom-1 left-1/2 -translate-x-1/2 h-0.5 bg-gradient-to-r from-gold to-gold-light rounded-full transition-all duration-300 ${
                      isActive ? 'w-[calc(100%-16px)] opacity-100' : 'w-0 opacity-0'
                    }`}
                  />
                </button>
              );
            })}
            <Button 
              onClick={() => scrollToSection("#donate")} 
              className="ml-3 btn-gold px-5 h-10"
            >
              Donate
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsOpen(!isOpen)} 
            className={`lg:hidden p-2.5 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 ${
              scrolled 
                ? 'text-foreground hover:bg-primary/10' 
                : 'text-white hover:bg-white/10'
            }`}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ease-out ${
          isOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
        }`}>
          <div className={`py-4 border-t ${scrolled ? 'border-border/50' : 'border-white/10'}`}>
            <div className="flex flex-col gap-1">
              {navLinks.map(link => {
                const isActive = activeSection === link.sectionId;
                return (
                  <button 
                    key={link.href} 
                    onClick={() => scrollToSection(link.href)} 
                    className={`px-4 py-3 text-left font-semibold text-[15px] rounded-xl transition-all duration-200 relative ${
                      scrolled 
                        ? `${isActive ? 'text-gold bg-gold/10' : 'text-foreground hover:bg-primary/10 hover:text-primary'}`
                        : `${isActive ? 'text-gold bg-gold/10' : 'text-white hover:bg-white/10 hover:text-gold'}`
                    }`}
                  >
                    {/* Active indicator bar */}
                    <span 
                      className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-r-full bg-gold transition-all duration-300 ${
                        isActive ? 'h-6 opacity-100' : 'h-0 opacity-0'
                      }`}
                    />
                    {link.label}
                  </button>
                );
              })}
              <Button 
                onClick={() => scrollToSection("#donate")} 
                className="mt-3 btn-gold h-11"
              >
                <Heart size={16} className="fill-current mr-2" />
                Donate Now
              </Button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;